import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { AccountManagerFilter } from "@/components/common/Filters/AccountManagerFilter"
import { StatusFilter } from "@/components/common/Filters/StatusFilter"
import { PermissionActionType } from "@/constants/permissions"
import type { CompanyListFilter } from "@/graphql/codegen/graphql"
import { CompanyStatus, CustomerAccountStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { CUSTOMER_ACCOUNT_STATUS_LABEL_MAP } from "@/screens/Companies/constants/customerAccountDataMap"
import { CompanyFilterSchema } from "@/screens/Companies/types/filters"
import { CUSTOMER_COMPANY_TYPES, type CompanyType } from "@/types/companyType"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

const statusList = Object.values(CompanyStatus)
const statusKey = "statuses"

export const CompanySearchFilter: FC = () => {
  const { hasPermissions } = usePermissionManager()
  const navigate = useNavigate({ from: "/app/companies" })
  const currentSearch = useSearch({
    from: "/app/companies/",
    strict: true,
  })

  const { statuses, accountManagers, types, customerAccountStatuses } = currentSearch

  const isCustomerCompanyType = types?.some((item) => CUSTOMER_COMPANY_TYPES.includes(item as CompanyType))

  const hasCustomerAccountPermission = hasPermissions([
    PermissionActionType.CUSTOMER_VIEW,
    PermissionActionType.CUSTOMER_MAINTENANCE,
  ])

  const methods = useForm<CompanyListFilter>({
    values: CompanyFilterSchema.parse({
      statuses: statuses ?? [],
      customerAccountStatuses: customerAccountStatuses ?? [],
      accountManagers: accountManagers ?? [],
      types: types ?? [],
    }),
  })

  const handleFilterChange = useCallback(
    (name: keyof CompanyListFilter, value: any) => {
      const params = getRefinedSearchParams(currentSearch, {
        ...currentSearch,
        [name]: value,
      })

      void navigate(params)
    },
    [currentSearch, navigate]
  )

  return (
    <FormProvider {...methods}>
      <Box className='flex h-full flex-col gap-y-2'>
        <Box className='flex-none'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Company Status
          </Typography>
          <Divider />
          <StatusFilter
            name={statusKey}
            data={statusList}
            control={methods.control}
            onFilterChange={handleFilterChange}
          />
        </Box>

        {isCustomerCompanyType && hasCustomerAccountPermission && (
          <Box className='flex-none'>
            <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
              Customer Account Status
            </Typography>
            <Divider />
            <StatusFilter
              name='customerAccountStatuses'
              data={Object.values(CustomerAccountStatus)}
              labelMap={CUSTOMER_ACCOUNT_STATUS_LABEL_MAP}
              control={methods.control}
              onFilterChange={handleFilterChange}
            />
          </Box>
        )}

        <Box className='grow overflow-hidden'>
          <AccountManagerFilter onFilterChange={handleFilterChange} />
        </Box>
      </Box>
    </FormProvider>
  )
}
